import { useContext, useState, useEffect } from 'react';
import { DownloadIcon } from '@heroicons/react/solid';
import mixpanel from 'mixpanel-browser';
import { Asset } from 'contentful';

import { UserContext } from '../../../context/userContext';
import { StarIcon, StrokeIcon } from '../../../Icons';
import { Button } from '../../Button';
import { ButtonLink } from '../../ButtonLink';
import { canShare, isIos } from '../../../helpers/shared';
import { Notification } from '../../Notification';
import { Spinner } from '../../Spinner';
import { ISectionInstall } from '../../../@types/generated/contentful';

type Props = {
  brush: ISectionInstall;
  image?: Asset;
  setTitle?: string;
  isLastItem?: boolean;
  onDownload: () => void;
};

const allowedFormats = {
  swatches: 'application/zip',
  ttf: 'font/ttf',
  png: 'image/png',
  usdz: 'application/zip',
  brush: 'application/zip',
  brushset: 'application/zip',
};

const ToolsListItem = ({ brush, image, isLastItem, setTitle, onDownload }: Props) => {
  const [isArSupported, setArSupported] = useState(false);
  const [isWarningVisible, setWarningVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useContext(UserContext);
  const { hasPremium = false } = user || {};

  const getZip = async (href: string) => {
    const response = await fetch(href);
    const buffer = await response.arrayBuffer();

    return buffer;
  };

  const matchFormats = /(brush|brushset|png|swatches|ttf|usdz)$/;
  const matches = brush.fields.installurl.match(matchFormats);
  const fileFormat = matches ? matches[0] : 'zip';
  const titleFormat = (title: string) => title.replace(/\s/g, '_');

  const sendToMixpanel = () => {
    mixpanel.track('Item Selected', {
      premium: hasPremium,
      set: setTitle,
      item: brush.fields.title,
      price: brush.fields.price,
    });
  };

  const handleButtonClick = () => {
    sendToMixpanel();
    setIsLoading(true);
    if (brush.fields.price.includes('Premium') && !hasPremium) {
      setIsLoading(false);
      onDownload();
    } else {
      getZip(brush.fields.installurl).then((r) => {
        const file = new File([r], `${titleFormat(brush.fields.title)}.${fileFormat}`, {
          type: allowedFormats[fileFormat as keyof typeof allowedFormats] || 'application/octet-stream',
        });
        if (navigator.share && file) {
          try {
            navigator
              .share({
                files: [file],
              })
              .finally(() => setIsLoading(false));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
          }
        }
      });
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    sendToMixpanel();
    if (brush.fields.price.includes('Premium') && !hasPremium) {
      e.preventDefault();
      onDownload();
    }
    if (isIos() && !canShare()) {
      setWarningVisible(true);
    }
  };

  useEffect(() => {
    const a = document.createElement('a');
    if (a.relList.supports('ar')) {
      setArSupported(true);
    }
  }, []);

  useEffect(() => {
    if (isWarningVisible) {
      const timer = setTimeout(() => setWarningVisible(false), 4000);
      return () => clearTimeout(timer);
    }
    return () => {};
  });

  return (
    <div className='flex justify-between items-center py-4 px-6 relative xs:px-4'>
      <div className='flex items-center'>
        <span className='inline-block relative shrink-0 rounded-full' style={{ backgroundColor: brush.fields.color }}>
          {(brush.fields.logo || image) && (
            <img
              className='h-16 w-16 rounded-full object-cover'
              src={brush.fields.logo || `http:${image?.fields.file.url}`}
              alt='brush'
              loading='lazy'
            />
          )}

          {brush.fields.price === 'Premium' && (
            <span
              className={`absolute 
      bottom-0 right-0 flex justify-center items-center p-[3px] rounded-full bg-main-blue text-white`}
            >
              <StarIcon className='w-[13px] h-[13px]' />
            </span>
          )}
        </span>
        <div className='text-left ml-5'>
          <p className='text-lg leading-lg font-bold mb-[2px]'>{brush.fields.title}</p>
          <p className='text-sm leading-sm'>{brush.fields.description}</p>
          {fileFormat === 'usdz' && isArSupported && (
            // eslint-disable-next-line react/no-invalid-html-attribute
            <a className='text-sm leading-sm text-main-blue' href={brush.fields.installurl} rel='ar'>
              <img alt='model preview' src={brush.fields.logo || `http:${image?.fields.file.url}`} className='hidden' />
              Show in AR
            </a>
          )}
        </div>
      </div>
      {/* @ts-ignore */}
      {canShare() && isIos() ? (
        <>
          <div className='w-[183px] ml-4 shrink-0 xs:hidden'>
            <Button theme='blue' size='small' onClick={handleButtonClick}>
              {isLoading ? <Spinner theme='blue' /> : <span>Download</span>}
            </Button>
          </div>
          <div className='w-[40px] ml-4 shrink-0 hidden xs:block'>
            <Button theme='blue' size='small' onClick={handleButtonClick}>
              {isLoading ? <Spinner theme='blue' /> : <DownloadIcon className='w-4 h-4' />}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className='w-[183px] ml-4 shrink-0 xs:hidden'>
            <ButtonLink theme='blue' size='small' href={brush.fields.installurl} onClick={handleLinkClick} download>
              Download
            </ButtonLink>
          </div>
          <div className='w-[40px] ml-4 shrink-0 hidden xs:block'>
            <ButtonLink theme='blue' size='small' onClick={handleLinkClick} href={brush.fields.installurl} download>
              <DownloadIcon className='w-4 h-4' />
            </ButtonLink>
          </div>
        </>
      )}
      {!isLastItem && (
        <div
          className={`left-[72px] 
        right-[32px] bottom-0 h-[1px] bg-main-grey-400 absolute xs:left-[16px] xs:right-[16px]`}
        />
      )}
      {isWarningVisible && (
        <Notification
          icon={<StrokeIcon className='w-[20px]' />}
          text='Content install is working only on iOS 15 and newer'
        />
      )}
    </div>
  );
};

export { ToolsListItem };
